// App-wide notification bell for workflow completions.
//
// There is no server push for workflow runs (the gateway reconciles run state
// from uTask; nothing streams it to the browser), so this polls the runs list,
// detects runs that have newly reached a TERMINAL state, and surfaces them as an
// unread-badged dropdown. Dedup + a first-load "baseline" (recording everything
// already finished WITHOUT notifying) live in localStorage so the user is never
// flooded with historical completions and the badge survives reloads.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { workflows as wf } from '../../services/api';
import { relTime } from './ui';

const POLL_MS = 25000;
const STORE_KEY = 'wf_notif_v1';
const MAX_ITEMS = 50;
const MAX_SEEN = 500;

// Terminal run states we notify on → 'success' | 'failure'. CANCELLED is omitted
// (the user cancelled it themselves); all non-terminal states are ignored.
const TERMINAL = {
  DONE: 'success',
  CRASHED: 'failure',
  BLOCKED: 'failure',
  BLOCKED_BADREQUEST: 'failure',
  BLOCKED_MAXRETRIES: 'failure',
  WONTFIX: 'failure',
};

function loadStore() {
  try {
    const p = JSON.parse(localStorage.getItem(STORE_KEY) || '');
    return {
      initialized: !!p.initialized,
      seen: Array.isArray(p.seen) ? p.seen : [],
      items: Array.isArray(p.items) ? p.items : [],
    };
  } catch {
    return { initialized: false, seen: [], items: [] };
  }
}

function saveStore(s) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({
      initialized: s.initialized,
      seen: s.seen.slice(-MAX_SEEN),
      items: s.items.slice(0, MAX_ITEMS),
    }));
  } catch {
    /* quota / disabled — non-fatal */
  }
}

// `inline`: drawn inside the top bar (2026-11-03) instead of floating fixed in the corner.
export default function WorkflowNotificationBell({ inline = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(() => loadStore().items);
  const storeRef = useRef(loadStore());
  const rootRef = useRef(null);

  const unread = items.reduce((n, it) => (it.read ? n : n + 1), 0);

  const poll = useCallback(async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    let runs;
    try {
      const res = await wf.listRuns({ limit: 50 });
      runs = Array.isArray(res.data) ? res.data : res.data?.data || [];
    } catch {
      return; // not entitled / offline — stay quiet
    }
    const store = storeRef.current;
    const seen = new Set(store.seen);
    const terminal = runs.filter((r) => r && TERMINAL[r.state]);

    // First ever poll: baseline everything already finished, no notifications.
    if (!store.initialized) {
      terminal.forEach((r) => seen.add(r.id));
      const next = { initialized: true, seen: [...seen], items: store.items };
      storeRef.current = next;
      saveStore(next);
      return;
    }

    const fresh = terminal.filter((r) => !seen.has(r.id));
    if (fresh.length === 0) return;

    const newItems = fresh.map((r) => {
      seen.add(r.id);
      return {
        id: r.id,
        title: r.title || 'Workflow run',
        state: r.state,
        kind: TERMINAL[r.state],
        at: r.updated_at || r.created_at || new Date().toISOString(),
        read: false,
      };
    });

    const merged = [...newItems, ...store.items].slice(0, MAX_ITEMS);
    const next = { initialized: true, seen: [...seen], items: merged };
    storeRef.current = next;
    saveStore(next);
    setItems(merged);

    // A gentle live nudge on top of the badge.
    if (fresh.length === 1) {
      const it = newItems[0];
      toast(`${it.kind === 'success' ? '✅' : '⚠️'} ${it.title} ${it.kind === 'success' ? 'completed' : 'failed'}`);
    } else {
      toast(`${fresh.length} workflows finished`);
    }
  }, []);

  useEffect(() => {
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [poll]);

  // Close the dropdown on an outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const persist = (items2) => {
    const next = { ...storeRef.current, items: items2 };
    storeRef.current = next;
    saveStore(next);
    setItems(items2);
  };
  const markAllRead = () => persist(storeRef.current.items.map((it) => ({ ...it, read: true })));
  const clearAll = () => persist([]);

  const toggle = () => {
    const nv = !open;
    setOpen(nv);
    if (nv && unread > 0) markAllRead(); // opening = seen
  };

  const openRun = (it) => {
    setOpen(false);
    navigate(`/workflows/runs/${it.id}`);
  };

  return (
    <div ref={rootRef} className={inline ? 'relative' : 'fixed top-4 right-16 z-40 lg:right-4'}>
      <button
        onClick={toggle}
        className={inline
          ? 'relative flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-800 hover:text-white'
          : 'relative flex h-10 w-10 items-center justify-center rounded-full border border-gray-700 bg-gray-900/90 text-gray-300 shadow-lg backdrop-blur transition-colors hover:bg-gray-800 hover:text-white'}
        title="Workflow notifications"
        aria-label="Workflow notifications"
      >
        <BellIcon className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-gray-800 bg-gray-900 shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-800 px-3 py-2">
            <div className="text-sm font-semibold text-gray-200">Workflow activity</div>
            {items.length > 0 && (
              <button className="text-xs text-gray-500 hover:text-gray-300" onClick={clearAll}>Clear</button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-gray-500">
              No completions yet. You&apos;ll be notified here when a workflow run finishes.
            </div>
          ) : (
            <ul className="max-h-96 divide-y divide-gray-800 overflow-y-auto">
              {items.map((it) => {
                const Icon = it.kind === 'success' ? CheckCircleIcon : XCircleIcon;
                return (
                  <li key={`${it.id}:${it.at}`}>
                    <button onClick={() => openRun(it)} className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-800/60">
                      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${it.kind === 'success' ? 'text-green-400' : 'text-red-400'}`} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-gray-200">{it.title}</div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          {it.kind === 'success' ? 'Completed' : `Finished — ${it.state}`} · {relTime(it.at)}
                        </div>
                      </div>
                      {!it.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
